import React, { useCallback, useContext, useState } from 'react';
import BoardSlideOut from 'components/BoardSlideOut';
import RecordsGrid from 'components/Grid/RecordsGrid';
import CardContext from '../../../contexts/CardContext';
import GridProvider from '../../../contextProviders/GridProvider';
import type { GridApi } from 'ag-grid-community';
import useBoardBaseView from '../../../hooks/useBoardBaseView';
import BaseViewsContext from '../../../contexts/BaseViewsContext';
import useWindowSize from '../../../hooks/useWindowSize';
import ReactPortal from '../../ReactPortal';
import BulkSalesCommissionAction from '../BulkSalesCommissionAction';
import { SALES_COMMISSION_ACTIONS_ELEMENT_ID } from '../constants';
import isPerformanceBoard from '../../../isPerformanceBoard';

const GridContainer = ({
  baseView,
  sortOverride,
  hideTotalsRow,
  gridApi,
  setGridApi,
  setExportHandle,
  isSizedToFitOverridden,
}: {
  baseView: FleetOps.BaseView;
  sortOverride?: SimpleGridSort[];
  hideTotalsRow: boolean;
  gridApi?: GridApi;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  setExportHandle: (handle: DataTable.ExportHandle | undefined) => void;
  isSizedToFitOverridden: boolean;
}) => {
  const { isMobile } = useWindowSize();
  const newBaseView = useBoardBaseView(baseView);
  const [isSalesCommission] = useState<boolean>(baseView.type === 'commission');
  const isFirstColumnFitted = isSizedToFitOverridden && !isMobile;
  // DataTable's api, for the board's drill highlight (AG's grid hands its
  // own to the GridProvider)
  const onGridReady = useCallback(
    ({ api }: { api: GridApi }) => setGridApi(api),
    [setGridApi],
  );

  return (
    <CardContext.Provider
      value={{
        i: '',
        w: 1,
        h: 1,
      }}
    >
      <GridProvider
        dataType={baseView.type}
        baseViewOverride={newBaseView}
        sortOverride={sortOverride}
        gridApi={gridApi}
        setGridApi={setGridApi}
      >
        <>
          {isSalesCommission && (
            <ReactPortal elementId={SALES_COMMISSION_ACTIONS_ELEMENT_ID}>
              <BulkSalesCommissionAction />
            </ReactPortal>
          )}
          <RecordsGrid
            surface="board"
            // AG fits the first column to its content and the others share
            // the rest when the board asks, else sizes every column to its
            // cells
            sizing={isFirstColumnFitted ? 'fitWidth' : 'fitContent'}
            skipHeaderWhenSizing={!isFirstColumnFitted}
            onGridReady={onGridReady}
            onExportHandle={setExportHandle}
            slideOut={<BoardSlideOut />}
            isBoard
            hideTotalsRow={hideTotalsRow}
            layoutOnFirstRender={true}
            layoutOnColumnChange={false}
            layoutOnModelUpdated={false}
            autoSizeFirstColumnOnly={isFirstColumnFitted}
            hasBorders={true}
            hasDarkHeader={true}
          />
        </>
      </GridProvider>
    </CardContext.Provider>
  );
};

const Gate = ({
  board,
  sortOverride,
  gridApi,
  setGridApi,
  setExportHandle,
}: {
  board: DriverPerformanceBoard | GeneralBoard | PerformanceBoardTypes.Board;
  sortOverride?: SimpleGridSort[];
  gridApi?: GridApi;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  setExportHandle: (handle: DataTable.ExportHandle | undefined) => void;
}) => {
  const { baseViews } = useContext(BaseViewsContext);
  const baseView = baseViews[board.dataType];

  if (baseView) {
    return (
      <GridContainer
        baseView={baseView}
        sortOverride={sortOverride}
        hideTotalsRow={
          isPerformanceBoard(board) ? false : !!board.hideTotalsRow
        }
        gridApi={gridApi}
        setGridApi={setGridApi}
        setExportHandle={setExportHandle}
        isSizedToFitOverridden={
          isPerformanceBoard(board) ? false : !!board.isSizedToFitOverridden
        }
      />
    );
  } else {
    return null;
  }
};

export default Gate;
